import type { TourId, TourState } from "../types/tour.types"

// Session storage key for persisting tour state across page navigations
export const TOUR_STATE_STORAGE_KEY = "turnus-tour-state"
const TOUR_PROGRESS_STORAGE_KEY = "turnus-tour-progress"

type TourProgress = {
  stepIndex: number
  stepRoutes: Record<number, string>
}

const getProgressMap = (): Partial<Record<TourId, TourProgress>> => {
  if (typeof globalThis.window === "undefined") {
    return {}
  }

  try {
    const raw = localStorage.getItem(TOUR_PROGRESS_STORAGE_KEY)
    if (!raw) return {}
    return JSON.parse(raw) as Partial<Record<TourId, TourProgress>>
  } catch {
    return {}
  }
}

const setProgressMap = (map: Partial<Record<TourId, TourProgress>>) => {
  if (typeof globalThis.window === "undefined") return
  localStorage.setItem(TOUR_PROGRESS_STORAGE_KEY, JSON.stringify(map))
}

export const tourStorage = {
  getState: (): TourState | null => {
    if (typeof globalThis.window === "undefined") {
      return null
    }

    const saved = sessionStorage.getItem(TOUR_STATE_STORAGE_KEY)
    if (!saved) return null

    try {
      return JSON.parse(saved) as TourState
    } catch {
      return null
    }
  },

  /**
   * Save tour state to session storage
   */
  setState: (state: TourState): void => {
    if (typeof globalThis.window === "undefined") return
    sessionStorage.setItem(TOUR_STATE_STORAGE_KEY, JSON.stringify(state))
  },

  /**
   * Clear tour state from session storage
   */
  clearState: (): void => {
    if (typeof globalThis.window === "undefined") return
    sessionStorage.removeItem(TOUR_STATE_STORAGE_KEY)
  },

  /**
   * Mark a specific tour as completed
   */
  markTourCompleted: (storageKey: string): void => {
    if (typeof globalThis.window === "undefined") return
    sessionStorage.setItem(storageKey, "true")
  },

  /**
   * Check if a specific tour is completed
   */
  isTourCompleted: (storageKey: string): boolean => {
    if (typeof globalThis.window === "undefined") return false
    return sessionStorage.getItem(storageKey) === "true"
  },

  /**
   * Set a demo flag in session storage
   */
  setDemoFlag: (flagKey: string, value: string = "true"): void => {
    if (typeof globalThis.window === "undefined") return
    sessionStorage.setItem(flagKey, value)
  },

  /**
   * Get a demo flag from session storage
   */
  getDemoFlag: (flagKey: string): string | null => {
    if (typeof globalThis.window === "undefined") return null
    return sessionStorage.getItem(flagKey)
  },

  saveProgress: (tourId: TourId, progress: TourProgress): void => {
    const existing = getProgressMap()
    const previous = existing[tourId]

    if (!previous || progress.stepIndex >= previous.stepIndex) {
      existing[tourId] = {
        stepIndex: progress.stepIndex,
        stepRoutes: {
          ...(previous?.stepRoutes ?? {}),
          ...progress.stepRoutes,
        },
      }
    } else {
      // Keep the furthest recorded step to avoid regressing progress
      existing[tourId] = previous
    }

    setProgressMap(existing)
  },

  getProgress: (tourId: TourId): TourProgress | null => {
    const existing = getProgressMap()
    return existing[tourId] ?? null
  },

  getAllProgress: (): Partial<Record<TourId, TourProgress>> => {
    return getProgressMap()
  },

  clearProgress: (tourId: TourId): void => {
    const existing = getProgressMap()
    if (existing[tourId]) {
      delete existing[tourId]
      setProgressMap(existing)
    }
  },
}
